/**
 * 返还房间信息
 */
import React, { useMemo } from 'react';
import { Table } from 'antd';
import { ColumnsType } from 'antd/es/table';
import intl from 'react-intl-universal';

import getRandomId from '@/utils/uuid';
import { Calculator, parseFen2Yuan } from '@/utils/number';
import { format2TimeStamp, format2DateStr } from '@/utils/date';
import ContentBlock from '@/components/Reservation/ContentBlock';
import InputWithCoupon from '@/components/Reservation/NumberInput/InputWithCoupon';
import CouponGapText from '@/components/Reservation/CouponGapText';
import { useDetailData } from '@/pages/reservation/reverseRollback/helper';
import { getOccSummaryText } from '@/pages/reservation/utils/formatter';

import styles from './index.module.scss';

const Index = () => {
  const { suborders, occType, occDuration } = useDetailData();

  // 单日房价
  const renderPriceItem = (value) => {
    return (
      <InputWithCoupon withCoupon value={value} couponValue={null} inputDisabled couponDisabled />
    );
  };

  const tableConfig = useMemo(() => {
    const columns: ColumnsType<any> = [
      { title: intl.get('drawer_extension_rollback_column_current_order'), dataIndex: 'resvNo' },
      {
        title: intl.get('drawer_extension_rollback_column_current_type'),
        dataIndex: 'roomTypeName',
      },
      {
        title: intl.get('drawer_extension_rollback_column_roll_type'),
        dataIndex: 'rollbackRoomTypeName',
      },
      { title: intl.get('drawer_extension_rollback_column_current_room'), dataIndex: 'roomNo' },
      {
        title: intl.get('drawer_extension_rollback_column_roll_room'),
        dataIndex: 'rollbackRoomNo',
      },
    ]; // 表格列
    const dataSource = []; // 表格数据
    const summary = [
      <Table.Summary.Cell index={0} key="0" colSpan={5}>
        {intl.get('reservation_room_summary_label')}
      </Table.Summary.Cell>,
    ]; // 小计

    const rentTimeMap: Record<string, number> = {}; // 入住日期
    const priceSumMap: Record<string, Calculator> = {}; // 每日房价
    const totalValue = new Calculator(); // 总价

    if (suborders && suborders.length) {
      suborders.forEach((roomItem) => {
        const rowData = { indexId: getRandomId(), ...roomItem };
        (roomItem.rentDayInfos || []).forEach((dayItem) => {
          const { rentDate, price } = dayItem;
          if (rentDate) {
            // 入住日期转时间戳
            const timestamp = format2TimeStamp(rentDate);

            // 当日房价总计
            const sumPrice = priceSumMap[timestamp] || new Calculator();
            sumPrice.plus(price || 0);
            priceSumMap[timestamp] = sumPrice;

            // 总房价
            totalValue.plus(price || 0);

            // 房间当日价格
            rowData[timestamp] = parseFen2Yuan(price);

            rentTimeMap[timestamp] = timestamp;
          }
        });
        dataSource.push(rowData);
      });
    }

    // 入住日期排序 后端乱序给的
    Object.values(rentTimeMap)
      .sort((prev, next) => prev - next)
      .forEach((item, idx) => {
        const sumPrice = priceSumMap[item];

        columns.push({
          title: <CouponGapText text={format2DateStr(item)} />,
          align: 'right',
          width: 260,
          dataIndex: item,
          render: renderPriceItem,
        });

        summary.push(
          <Table.Summary.Cell align="right" index={1 + idx} key={item}>
            <CouponGapText text={parseFen2Yuan(sumPrice.getValue())} />
          </Table.Summary.Cell>,
        );
      });

    return {
      columns,
      dataSource,
      summary,
      tableFooter: getOccSummaryText({
        count: dataSource.length,
        type: occType,
        duration: occDuration,
        amount: parseFen2Yuan(totalValue.getValue()),
      }),
    };
  }, [suborders, occType]);

  return (
    <ContentBlock
      title={intl.get('drawer_extension_rollback_block_room')}
      className={styles.roomInfoWrapper}
    >
      <Table
        className={styles.tableContent}
        scroll={{ x: 'max-content' }}
        rowKey="indexId"
        pagination={false}
        columns={tableConfig.columns}
        dataSource={tableConfig.dataSource}
        summary={() => (
          <Table.Summary fixed>
            <Table.Summary.Row>{tableConfig.summary}</Table.Summary.Row>
          </Table.Summary>
        )}
        footer={() => tableConfig.tableFooter}
      />
    </ContentBlock>
  );
};

export default Index;
